import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Leaf, LogOut, LayoutDashboard, CalendarDays, Repeat, MapPinOff, Mail, Star, Image as ImageIcon,
  Gift, CheckCircle2, XCircle, DollarSign, TrendingUp, Users, Loader2, Printer, Download, Trash2, Upload, Settings, AlertTriangle,
  KeyRound, Pencil, Plus, Minus, ShieldAlert, X, FileText, Wrench,
} from "lucide-react";
import api, { API } from "@/lib/api";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import InvoiceTab from "./InvoiceTab";
import ServicesTab from "./ServicesTab";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const SLOT_OPTIONS = [
  ["10:00", "10:00 AM – 12:00 PM"],
  ["12:00", "12:00 PM – 2:00 PM"],
  ["14:00", "2:00 PM – 4:00 PM"],
  ["16:00", "4:00 PM – 6:00 PM"],
  ["18:00", "6:00 PM – 8:00 PM"],
];

const TABS = [
  { id: "bookings", label: "Bookings", icon: CalendarDays },
  { id: "customers", label: "Customers", icon: Users },
  { id: "invoices", label: "Invoices", icon: FileText },
  { id: "services", label: "Services & Rates", icon: Wrench },
  { id: "subscriptions", label: "Subscriptions", icon: Repeat },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "referrals", label: "Referrals", icon: Gift },
  { id: "leads", label: "Out-of-Area Leads", icon: MapPinOff },
  { id: "contacts", label: "Messages", icon: Mail },
  { id: "settings", label: "Schedule", icon: Settings },
  { id: "account", label: "Account & Data", icon: KeyRound },
];

const HEADERS = {
  bookings: ["Customer", "Plan", "Slot", "Pickup", "Notes", "Payment", "Status", "Actions"],
  customers: ["Name", "Email", "Phone", "Bookings", "Free-Visit Credits", "Actions"],
  subscriptions: ["Customer", "Plan", "Bills Paid", "Minimum", "Cancelable?", "Status"],
  reviews: ["Customer", "Rating", "Review", "Flag", "Status", "Actions"],
  referrals: ["Referrer", "Referred Customer", "Reward", "Date"],
  contacts: ["Name", "Contact", "Address", "ZIP", "Message", "Received"],
  messages: ["Name", "Email", "Phone", "Message", "Status", "Action"],
};

const STATUS_STYLES = {
  confirmed: "bg-accent text-accent-foreground",
  complete: "bg-primary text-primary-foreground",
  skipped: "bg-muted text-muted-foreground",
  pending: "bg-muted text-muted-foreground",
  cancelled: "bg-destructive/15 text-destructive",
  needs_reschedule: "bg-destructive/15 text-destructive",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [subs, setSubs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [dayFilter, setDayFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [gForm, setGForm] = useState({ before: null, after: null, caption: "" });
  const [uploading, setUploading] = useState(false);

  const [rsTarget, setRsTarget] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [editBooking, setEditBooking] = useState(null);
  const [editCust, setEditCust] = useState(null);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [resetText, setResetText] = useState("");
  const [resetPw, setResetPw] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  const logout = () => { localStorage.removeItem("rmb_token"); nav("/admin"); };

  const loadAll = useCallback(async () => {
    try {
      const [s, b, sub, rv, g, rf, l, c, st, cust] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings", { params: dayFilter ? { day: dayFilter } : {} }),
        api.get("/admin/subscriptions"),
        api.get("/admin/reviews"),
        api.get("/admin/gallery"),
        api.get("/admin/referrals"),
        api.get("/admin/leads"),
        api.get("/admin/contacts"),
        api.get("/admin/settings"),
        api.get("/admin/customers"),
      ]);
      setStats(s.data); setBookings(b.data.bookings); setSubs(sub.data.subscriptions);
      setReviews(rv.data.reviews); setGallery(g.data.items); setReferrals(rf.data.referrals);
      setLeads(l.data.leads); setContacts(c.data.contacts); setSettings(st.data);
      setCustomers(cust.data.customers);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) logout();
      else toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [dayFilter]);

  useEffect(() => {
    if (!localStorage.getItem("rmb_token")) { nav("/admin"); return; }
    loadAll();
  }, [loadAll, nav]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      const s = await api.get("/admin/stats"); setStats(s.data);
      toast.success(`Marked ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const setReview = async (id, status) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { status });
      setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Review ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const toggleDay = async (idx) => {
    const cur = settings?.active_days || [];
    const next = cur.includes(idx) ? cur.filter((d) => d !== idx) : [...cur, idx].sort((a, b) => a - b);
    setSettings({ ...settings, active_days: next });
    try { await api.patch("/admin/settings", { active_days: next }); toast.success("Schedule updated"); }
    catch { setSettings({ ...settings, active_days: cur }); toast.error("Update failed"); }
  };

  const markReschedule = async (id, reason) => {
    setRsTarget(null);
    try {
      await api.patch(`/admin/bookings/${id}`, { status: "needs_reschedule", reason });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status: "needs_reschedule", reschedule_history: [...(b.reschedule_history || []), { reason, at: new Date().toISOString() }] } : b)));
      toast.success("Customer notified to reschedule");
    } catch { toast.error("Update failed"); }
  };

  const waiveFee = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/waive-fee`);
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, free_reschedule_credits: (b.free_reschedule_credits || 0) + 1 } : b)));
      toast.success("Reschedule fee waived — free reschedule granted");
    } catch { toast.error("Failed to waive fee"); }
  };

  const markContact = async (id) => {
    try {
      await api.patch(`/admin/contacts/${id}`, { status: "addressed" });
      setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, status: "addressed" } : c)));
      toast.success("Marked addressed");
    } catch { toast.error("Update failed"); }
  };

  const uploadGallery = async (e) => {
    e.preventDefault();
    if (!gForm.before || !gForm.after) { toast.error("Pick both a before and after photo"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("before", gForm.before);
    fd.append("after", gForm.after);
    fd.append("caption", gForm.caption);
    try {
      await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added to gallery");
      setGForm({ before: null, after: null, caption: "" });
      const g = await api.get("/admin/gallery"); setGallery(g.data.items);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const deleteGallery = async (id) => {
    try {
      await api.delete(`/admin/gallery/${id}`);
      setGallery((gs) => gs.filter((g) => g.id !== id));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  const saveBooking = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}/details`, patch);
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, ...data.booking } : b)));
      setEditBooking(null);
      toast.success("Booking updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update booking"); }
  };

  const saveCustomer = async (id, patch) => {
    try {
      await api.patch(`/admin/customers/${id}`, patch);
      setCustomers((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
      setEditCust(null);
      toast.success("Customer updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update customer"); }
  };

  const creditCustomer = async (id, delta) => {
    try {
      const { data } = await api.post(`/admin/customers/${id}/credit`, { delta });
      setCustomers((cs) => cs.map((c) => (c.id === id ? { ...c, free_visit_credits: data.free_visit_credits } : c)));
      toast.success(delta > 0 ? "Credit added" : "Credit removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update credits"); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) { toast.error("New passwords do not match"); return; }
    if (pwForm.next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      setPwForm({ current: "", next: "", confirm: "" });
      toast.success("Password changed successfully");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change password"); }
    finally { setPwBusy(false); }
  };

  const resetData = async () => {
    setResetBusy(true);
    try {
      await api.post("/admin/reset-data", { confirm: "DELETE", password: resetPw });
      setResetText("");
      setResetPw("");
      toast.success("All customer data cleared — fresh slate!");
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    finally { setResetBusy(false); }
  };

  // Route sheet: sort day's confirmed stops by zip + address
  const routeStops = () =>
    bookings
      .filter((b) => ["confirmed", "complete"].includes(b.status))
      .sort((a, b) => (a.zip + a.address).localeCompare(b.zip + b.address));

  const exportRouteCsv = () => {
    const stops = routeStops();
    if (stops.length === 0) { toast.info("No stops to export"); return; }
    const rows = [["Order", "Name", "Phone", "Address", "ZIP", "Window", "Bins", "Notes", "Status"]];
    stops.forEach((b, i) => rows.push([i + 1, b.name, b.phone, b.address, b.zip, b.slot_label, b.num_bins, (b.notes || "").replace(/\n/g, " "), b.status]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `route-${dayFilter || "all"}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const printRoute = () => {
    const stops = routeStops();
    if (stops.length === 0) { toast.info("No stops to print"); return; }
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to print the route sheet."); return; }
    const doc = w.document;
    doc.title = `Route ${dayFilter || ""}`;
    const style = doc.createElement("style");
    style.textContent = "body{font-family:Arial;padding:24px}h1{margin:0 0 4px}table{width:100%;border-collapse:collapse;margin-top:16px}td,th{border:1px solid #ddd;padding:8px;text-align:left;font-size:13px}th{background:#16a34a;color:#fff}";
    doc.head.appendChild(style);
    const h1 = doc.createElement("h1");
    h1.textContent = "Bins Plus Pressure Washing — Route Sheet";
    const sub = doc.createElement("div");
    sub.textContent = `${dayFilter || "All upcoming"} · ${stops.length} stops`;
    doc.body.appendChild(h1);
    doc.body.appendChild(sub);
    const table = doc.createElement("table");
    const td = (text) => { const el = doc.createElement("td"); el.textContent = text; return el; };
    const headRow = doc.createElement("tr");
    ["#", "Customer", "Address", "ZIP", "Window", "Bins", "Notes"].forEach((h) => {
      const th = doc.createElement("th"); th.textContent = h; headRow.appendChild(th);
    });
    table.appendChild(headRow);
    // textContent auto-escapes untrusted customer input (name/address/notes) — no XSS.
    stops.forEach((b, i) => {
      const tr = doc.createElement("tr");
      tr.appendChild(td(String(i + 1)));
      const cust = doc.createElement("td");
      cust.appendChild(doc.createTextNode(b.name || ""));
      cust.appendChild(doc.createElement("br"));
      cust.appendChild(doc.createTextNode(b.phone || ""));
      tr.appendChild(cust);
      tr.appendChild(td(b.address || ""));
      tr.appendChild(td(b.zip || ""));
      tr.appendChild(td(b.slot_label || ""));
      tr.appendChild(td(String(b.num_bins ?? "")));
      tr.appendChild(td(b.notes || ""));
      table.appendChild(tr);
    });
    doc.body.appendChild(table);
    doc.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-card lg:flex">
        <div className="flex items-center gap-2 border-b border-border px-6 py-5">
          <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-xl"><img src="/logo-bins-plus.png" alt="Bins Plus" className="h-9 w-9 object-contain" /></span>
          <span className="font-heading text-lg font-extrabold">Bins Plus Admin</span>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"}`}>
              <t.icon className="h-5 w-5" /> {t.label}
            </button>
          ))}
        </nav>
        <button data-testid="admin-logout" onClick={logout}
          className="m-3 flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-muted-foreground hover:bg-accent">
          <LogOut className="h-5 w-5" /> Log out
        </button>
      </aside>

      <div className="flex-1 overflow-x-hidden">
        <header className="flex items-center justify-between border-b border-border px-5 py-4 lg:px-8">
          <div className="flex items-center gap-2"><LayoutDashboard className="h-5 w-5 text-primary" /><h1 className="font-heading text-xl font-bold">{TABS.find((t) => t.id === tab)?.label || "Dashboard"}</h1></div>
          <div className="flex flex-wrap gap-2 lg:hidden">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`rounded-lg p-2 ${tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                <t.icon className="h-5 w-5" />
              </button>
            ))}
            <button onClick={logout} className="rounded-lg p-2 text-muted-foreground"><LogOut className="h-5 w-5" /></button>
          </div>
        </header>

        <div className="p-5 lg:p-8">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : (
            <>
              {stats && tab !== "account" && (
                <div className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                  <StatCard icon={CalendarDays} label="Total Bookings" value={stats.total_bookings} />
                  <StatCard icon={TrendingUp} label="Upcoming Visits" value={stats.upcoming} />
                  <StatCard icon={Repeat} label="Active Subs" value={stats.active_subscriptions} />
                  <StatCard icon={Users} label="Leads" value={stats.leads} />
                  <StatCard icon={DollarSign} label="Revenue" value={`$${stats.revenue.toFixed(2)}`} />
                </div>
              )}

              {tab === "bookings" && (
                <div>
                  <div className="mb-4 flex flex-wrap items-center gap-3">
                    <label className="text-sm font-semibold">Route day:</label>
                    <input data-testid="admin-day-filter" type="date" value={dayFilter} onChange={(e) => setDayFilter(e.target.value)}
                      className="rounded-xl border border-border bg-muted/50 px-3 py-2 text-sm" />
                    {dayFilter && <button onClick={() => setDayFilter("")} className="text-sm font-semibold text-primary">Clear</button>}
                    <div className="ml-auto flex gap-2">
                      <button data-testid="route-export" onClick={exportRouteCsv} className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-accent"><Download className="h-4 w-4" /> Export CSV</button>
                      <button data-testid="route-print" onClick={printRoute} className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:brightness-110"><Printer className="h-4 w-4" /> Print Route</button>
                    </div>
                  </div>
                  <Table headers={HEADERS.bookings}>
                    {bookings.length === 0 && <EmptyRow cols={8} text="No bookings yet." />}
                    {bookings.map((b) => (
                      <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-border/50">
                        <Td><div className="font-semibold">{b.name}</div><div className="text-xs text-muted-foreground">{b.phone}</div><div className="text-xs text-muted-foreground">{b.address}, {b.zip}</div></Td>
                        <Td>{b.plan_name}<div className="text-xs text-muted-foreground">{b.num_bins} bins</div></Td>
                        <Td>{b.slot_date}<div className="text-xs text-muted-foreground">{b.slot_label}</div></Td>
                        <Td>{b.pickup_day || "—"}</Td>
                        <Td><span className="line-clamp-2 max-w-[180px] text-xs">{b.notes || "—"}</span></Td>
                        <Td><span className={`rounded-full px-2 py-1 text-xs font-bold ${b.payment_status === "paid" ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"}`}>{b.payment_status}</span><div className="mt-1 text-xs font-semibold">${b.amount?.toFixed(2)}</div></Td>
                        <Td><span className={`rounded-full px-2 py-1 text-xs font-bold ${STATUS_STYLES[b.status] || "bg-muted"}`}>{b.status}</span>{b.reschedule_history?.length ? <span data-testid={`reason-history-${b.id}`} title={b.reschedule_history.map((h) => h.reason.replace(/_/g, " ")).join(", ")} className="ml-1 inline-block rounded-full bg-destructive/15 px-1.5 py-0.5 text-[10px] font-bold text-destructive">↻{b.reschedule_history.length}</span> : null}</Td>
                        <Td><div className="relative flex gap-1">
                          <button data-testid={`edit-booking-${b.id}`} onClick={() => setEditBooking(b)} title="Edit booking details" className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent"><Pencil className="h-4 w-4" /></button>
                          <button data-testid={`mark-complete-${b.id}`} onClick={() => setStatus(b.id, "complete")} title="Mark complete" className="rounded-lg p-1.5 text-primary hover:bg-accent"><CheckCircle2 className="h-4 w-4" /></button>
                          <button data-testid={`mark-skipped-${b.id}`} onClick={() => setStatus(b.id, "skipped")} title="Mark skipped" className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent"><XCircle className="h-4 w-4" /></button>
                          <button data-testid={`mark-reschedule-${b.id}`} onClick={() => setRsTarget(rsTarget === b.id ? null : b.id)} title="Couldn't clean — notify to reschedule" className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"><AlertTriangle className="h-4 w-4" /></button>
                          <button data-testid={`waive-fee-${b.id}`} onClick={() => waiveFee(b.id)} title="Waive the $10 reschedule fee (grant a free reschedule)" className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent"><DollarSign className="h-4 w-4" /></button>
                          {(b.free_reschedule_credits || 0) > 0 && <span className="self-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground" title="Free reschedule credits">{b.free_reschedule_credits} free</span>}
                          {rsTarget === b.id && (
                            <div data-testid={`reason-menu-${b.id}`} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setRsTarget(null)}>
                              <div className="w-full max-w-xs rounded-2xl border border-border bg-card p-2 shadow-xl" onClick={(e) => e.stopPropagation()}>
                                <p className="px-3 py-2 text-sm font-semibold">Why couldn't we clean? (emails customer)</p>
                                {[["bags_in_bin", "Trash bags in bin"], ["blocked_access", "Blocked access"], ["gate_locked", "Gate locked"], ["other", "Other"]].map(([val, lbl]) => (
                                  <button key={val} data-testid={`reason-${val}-${b.id}`} onClick={() => markReschedule(b.id, val)} className="block w-full rounded-lg px-3 py-2.5 text-left text-sm font-semibold hover:bg-accent">{lbl}</button>
                                ))}
                                <button onClick={() => setRsTarget(null)} className="mt-1 block w-full rounded-lg px-3 py-2 text-center text-xs font-semibold text-muted-foreground hover:bg-accent">Cancel</button>
                              </div>
                            </div>
                          )}
                        </div></Td>
                      </tr>
                    ))}
                  </Table>
                </div>
              )}

              {tab === "customers" && (
                <Table headers={HEADERS.customers}>
                  {customers.length === 0 && <EmptyRow cols={6} text="No customers yet." />}
                  {customers.map((c) => (
                    <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-b border-border/50">
                      <Td className="font-semibold">{c.name || "—"}</Td>
                      <Td>{c.email}</Td>
                      <Td>{c.phone || "—"}</Td>
                      <Td>{c.booking_count}</Td>
                      <Td>
                        <div className="flex items-center gap-2">
                          <button data-testid={`credit-minus-${c.id}`} onClick={() => creditCustomer(c.id, -1)} className="rounded-lg border border-border p-1 hover:bg-accent"><Minus className="h-3.5 w-3.5" /></button>
                          <span data-testid={`credit-value-${c.id}`} className="min-w-[1.5rem] text-center font-bold">{c.free_visit_credits || 0}</span>
                          <button data-testid={`credit-plus-${c.id}`} onClick={() => creditCustomer(c.id, 1)} className="rounded-lg border border-border p-1 hover:bg-accent"><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                      </Td>
                      <Td>
                        <button data-testid={`edit-customer-${c.id}`} onClick={() => setEditCust(c)} title="Edit customer" className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent"><Pencil className="h-4 w-4" /></button>
                      </Td>
                    </tr>
                  ))}
                </Table>
              )}

              {tab === "invoices" && <InvoiceTab />}

              {tab === "services" && <ServicesTab />}

              {tab === "subscriptions" && (
                <Table headers={HEADERS.subscriptions}>
                  {subs.length === 0 && <EmptyRow cols={6} text="No active subscriptions yet." />}
                  {subs.map((s) => (
                    <tr key={s.id} className="border-b border-border/50" data-testid={`sub-row-${s.id}`}>
                      <Td><div className="font-semibold">{s.name}</div><div className="text-xs text-muted-foreground">{s.email}</div></Td>
                      <Td>{s.plan_name}</Td><Td>{s.bills_count}</Td><Td>{s.min_bills}</Td>
                      <Td>{s.bills_count >= s.min_bills ? <span className="font-semibold text-primary">Yes</span> : <span className="text-muted-foreground">Not yet</span>}</Td>
                      <Td><span className={`rounded-full px-2 py-1 text-xs font-bold ${s.status === "active" ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"}`}>{s.status}</span></Td>
                    </tr>
                  ))}
                </Table>
              )}

              {tab === "reviews" && (
                <Table headers={HEADERS.reviews}>
                  {reviews.length === 0 && <EmptyRow cols={6} text="No reviews yet." />}
                  {reviews.map((r) => (
                    <tr key={r.id} className={`border-b border-border/50 ${r.flagged ? "bg-destructive/5" : ""}`} data-testid={`review-row-${r.id}`}>
                      <Td><div className="font-semibold">{r.name}</div><div className="text-xs text-muted-foreground">{r.email}</div>{r.location && <div className="text-xs text-muted-foreground">{r.location}</div>}</Td>
                      <Td><span className="whitespace-nowrap">{"★".repeat(r.rating)}<span className="text-muted-foreground/40">{"☆".repeat(5 - r.rating)}</span></span> <span className="text-xs text-muted-foreground">{r.rating}/5</span></Td>
                      <Td><span className="line-clamp-3 max-w-[260px] text-xs">{r.text}</span></Td>
                      <Td>{r.flagged ? <span className="rounded-full bg-destructive/15 px-2 py-1 text-xs font-bold text-destructive">Negative — follow up</span> : "—"}</Td>
                      <Td><span className={`rounded-full px-2 py-1 text-xs font-bold ${r.status === "approved" ? "bg-primary text-primary-foreground" : r.status === "rejected" ? "bg-muted text-muted-foreground" : "bg-accent text-accent-foreground"}`}>{r.status}</span></Td>
                      <Td><div className="flex gap-1">
                        <button data-testid={`review-approve-${r.id}`} onClick={() => setReview(r.id, "approved")} title="Approve & post" className="rounded-lg p-1.5 text-primary hover:bg-accent"><CheckCircle2 className="h-4 w-4" /></button>
                        <button data-testid={`review-reject-${r.id}`} onClick={() => setReview(r.id, "rejected")} title="Reject" className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent"><XCircle className="h-4 w-4" /></button>
                      </div></Td>
                    </tr>
                  ))}
                </Table>
              )}

              {tab === "gallery" && (
                <div>
                  <form onSubmit={uploadGallery} className="mb-6 rounded-2xl border border-border bg-card p-6">
                    <h3 className="font-heading text-lg font-bold">Add a before &amp; after</h3>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <label className="cursor-pointer rounded-xl border border-dashed border-border p-4 text-sm">
                        <span className="font-semibold">Before photo</span>
                        <input data-testid="gallery-before" type="file" accept="image/*" className="mt-2 block w-full text-xs" onChange={(e) => setGForm({ ...gForm, before: e.target.files[0] })} />
                      </label>
                      <label className="cursor-pointer rounded-xl border border-dashed border-border p-4 text-sm">
                        <span className="font-semibold">After photo</span>
                        <input data-testid="gallery-after" type="file" accept="image/*" className="mt-2 block w-full text-xs" onChange={(e) => setGForm({ ...gForm, after: e.target.files[0] })} />
                      </label>
                    </div>
                    <input data-testid="gallery-caption" placeholder="Caption (e.g. Waldorf trash bin)" value={gForm.caption} onChange={(e) => setGForm({ ...gForm, caption: e.target.value })}
                      className="mt-4 w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
                    <button data-testid="gallery-upload" type="submit" disabled={uploading}
                      className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 font-bold text-primary-foreground hover:brightness-110 disabled:opacity-50">
                      {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Upload className="h-4 w-4" /> Upload</>}
                    </button>
                  </form>
                  {gallery.length === 0 ? (
                    <p className="py-10 text-center text-muted-foreground">No gallery photos yet.</p>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {gallery.map((g) => (
                        <div key={g.id} data-testid={`gallery-admin-${g.id}`} className="overflow-hidden rounded-2xl border border-border bg-card">
                          <div className="grid grid-cols-2">
                            <img src={`${API}/gallery/image/${g.id}/before`} alt="Before" className="h-32 w-full object-cover" />
                            <img src={`${API}/gallery/image/${g.id}/after`} alt="After" className="h-32 w-full object-cover" />
                          </div>
                          <div className="flex items-center justify-between p-3">
                            <span className="text-sm font-semibold">{g.caption || "Untitled"}</span>
                            <button data-testid={`gallery-delete-${g.id}`} onClick={() => deleteGallery(g.id)} className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {tab === "referrals" && (
                <Table headers={HEADERS.referrals}>
                  {referrals.length === 0 && <EmptyRow cols={4} text="No referrals yet." />}
                  {referrals.map((r) => (
                    <tr key={r.id} className="border-b border-border/50" data-testid={`referral-row-${r.id}`}>
                      <Td><div className="font-semibold">{r.referrer_name || "—"}</div><div className="text-xs text-muted-foreground">{r.referrer_email}</div></Td>
                      <Td>{r.referred_email}</Td>
                      <Td>{r.status === "applied_to_subscription" ? <span className="font-semibold text-primary">Applied (free next invoice)</span> : <span className="text-muted-foreground">Credit pending — comp a visit</span>}</Td>
                      <Td className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</Td>
                    </tr>
                  ))}
                </Table>
              )}

              {tab === "settings" && (
                <div className="max-w-lg rounded-2xl border border-border bg-card p-6" data-testid="settings-panel">
                  <h3 className="font-heading text-lg font-bold">Booking days</h3>
                  <p className="mt-1 text-sm text-muted-foreground">Turn the days customers can book on or off. Service window: 10am–8pm.</p>
                  {settings && (settings.active_days || []).length === 0 && (
                    <div data-testid="empty-days-warning" className="mt-4 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
                      ⚠️ All days are OFF — customers cannot book any visits right now. Turn at least one day on.
                    </div>
                  )}
                  <div className="mt-4 space-y-2">
                    {DAYS.map((d, i) => {
                      const on = (settings?.active_days || []).includes(i);
                      return (
                        <button key={d} data-testid={`day-toggle-${i}`} onClick={() => toggleDay(i)}
                          className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${on ? "border-primary bg-accent text-accent-foreground" : "border-border text-muted-foreground hover:bg-accent/50"}`}>
                          {d}
                          <span className={`rounded-full px-3 py-0.5 text-xs font-bold ${on ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{on ? "On" : "Off"}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {tab === "leads" && (
                <Table headers={HEADERS.contacts}>
                  {leads.length === 0 && <EmptyRow cols={6} text="No out-of-area leads." />}
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-border/50" data-testid={`lead-row-${l.id}`}>
                      <Td className="font-semibold">{l.name}</Td>
                      <Td><div>{l.phone}</div><div className="text-xs text-muted-foreground">{l.email}</div></Td>
                      <Td>{l.address}</Td><Td>{l.zip}</Td>
                      <Td><span className="line-clamp-2 max-w-[200px] text-xs">{l.notes || "—"}</span></Td>
                      <Td className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</Td>
                    </tr>
                  ))}
                </Table>
              )}

              {tab === "contacts" && (
                <Table headers={HEADERS.messages}>
                  {contacts.length === 0 && <EmptyRow cols={6} text="No messages yet." />}
                  {contacts.map((c) => (
                    <tr key={c.id} className="border-b border-border/50" data-testid={`contact-row-${c.id}`}>
                      <Td className="font-semibold">{c.name}</Td><Td>{c.email}</Td><Td>{c.phone || "—"}</Td>
                      <Td><span className="line-clamp-2 max-w-[260px] text-xs">{c.message}</span></Td>
                      <Td><span className={`rounded-full px-2 py-1 text-xs font-bold ${c.status === "addressed" ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"}`}>{c.status || "new"}</span></Td>
                      <Td>{c.status !== "addressed" && <button data-testid={`contact-address-${c.id}`} onClick={() => markContact(c.id)} className="rounded-lg px-2 py-1 text-xs font-bold text-primary hover:bg-accent">Mark addressed</button>}</Td>
                    </tr>
                  ))}
                </Table>
              )}
              {tab === "account" && (
                <div className="max-w-lg space-y-6" data-testid="account-panel">
                  <form onSubmit={changePassword} className="rounded-2xl border border-border bg-card p-6" data-testid="change-password-form">
                    <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /><h3 className="font-heading text-lg font-bold">Change your password</h3></div>
                    <p className="mt-1 text-sm text-muted-foreground">Update the password for your admin login. Choose something private and at least 8 characters.</p>
                    <input data-testid="pw-current" type="password" required placeholder="Current password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                      className="mt-4 w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
                    <input data-testid="pw-new" type="password" required placeholder="New password (min 8 chars)" value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                      className="mt-3 w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
                    <input data-testid="pw-confirm" type="password" required placeholder="Confirm new password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                      className="mt-3 w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
                    <button data-testid="pw-submit" type="submit" disabled={pwBusy}
                      className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 font-bold text-primary-foreground hover:brightness-110 disabled:opacity-50">
                      {pwBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update Password"}
                    </button>
                  </form>

                  <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6" data-testid="danger-zone">
                    <div className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-destructive" /><h3 className="font-heading text-lg font-bold text-destructive">Danger zone — reset all data</h3></div>
                    <p className="mt-1 text-sm text-muted-foreground">Permanently deletes <strong>all customers, bookings, subscriptions, leads, messages, referrals, payments, reviews and gallery photos</strong>. Your admin account and schedule settings are kept. This cannot be undone — use it to start with a clean slate. For safety you must re-enter your admin password.</p>
                    <label className="mt-4 block text-sm font-semibold">Type <span className="rounded bg-muted px-1 font-mono">DELETE</span> to confirm</label>
                    <input data-testid="reset-confirm-input" value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="DELETE"
                      className="mt-2 w-full rounded-xl border border-border bg-card px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-destructive" />
                    <label className="mt-3 block text-sm font-semibold">Re-enter your admin password</label>
                    <input data-testid="reset-password-input" type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Your admin password"
                      className="mt-2 w-full rounded-xl border border-border bg-card px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-destructive" />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid="reset-data-btn" disabled={resetBusy || resetText !== "DELETE" || !resetPw}
                          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-destructive px-6 py-3 font-bold text-destructive-foreground hover:brightness-110 disabled:opacity-40">
                          {resetBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Trash2 className="h-4 w-4" /> Clear All Data</>}
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="reset-confirm-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Permanently delete all data?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This deletes ALL customers, bookings, subscriptions, leads, messages, referrals, payments, reviews and gallery photos. Your admin account and settings are kept. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="reset-cancel-btn">Cancel</AlertDialogCancel>
                          <AlertDialogAction data-testid="reset-confirm-btn" onClick={resetData}
                            className="bg-destructive text-destructive-foreground hover:brightness-110">
                            Yes, delete everything
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {editBooking && <EditBookingModal booking={editBooking} onClose={() => setEditBooking(null)} onSave={saveBooking} />}
      {editCust && <EditCustomerModal customer={editCust} onClose={() => setEditCust(null)} onSave={saveCustomer} />}
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center justify-between"><span className="text-sm font-semibold text-muted-foreground">{label}</span><Icon className="h-5 w-5 text-primary" /></div>
      <div className="mt-2 font-heading text-2xl font-extrabold">{value}</div>
    </div>
  );
}

function Table({ headers, children }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card">
      <table className="w-full text-left text-sm">
        <thead><tr className="border-b border-border bg-muted/40">{headers.map((h) => <th key={h} className="px-4 py-3 font-semibold text-muted-foreground">{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}

function EmptyRow({ cols, text }) {
  return <tr><td colSpan={cols} className="px-4 py-10 text-center text-muted-foreground">{text}</td></tr>;
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div data-testid="edit-modal" className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold">{title}</h3>
          <button data-testid="edit-modal-close" onClick={onClose} className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold">{label}</span>
      <input {...props} className="mt-1 w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
    </label>
  );
}

function EditBookingModal({ booking, onClose, onSave }) {
  const [f, setF] = useState({
    name: booking.name || "", email: booking.email || "", phone: booking.phone || "",
    address: booking.address || "", zip: booking.zip || "", pickup_day: booking.pickup_day || "",
    notes: booking.notes || "", slot_date: booking.slot_date || "", slot_time: booking.slot_time || "",
    num_bins: booking.num_bins || 1,
  });
  const [avail, setAvail] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    if (!f.slot_date || !f.slot_time) { setAvail(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/slot-availability", {
          params: { slot_date: f.slot_date, slot_time: f.slot_time, exclude_id: booking.id },
        });
        if (!cancelled) setAvail(data);
      } catch { if (!cancelled) setAvail(null); }
    })();
    return () => { cancelled = true; };
  }, [f.slot_date, f.slot_time, booking.id]);

  const submit = (e) => {
    e.preventDefault();
    if (avail?.full && !window.confirm(`Heads up: that time slot is already full (${avail.booked} of ${avail.capacity} booked). Book this visit into it anyway?`)) return;
    onSave(booking.id, { ...f, num_bins: parseInt(f.num_bins, 10) });
  };
  return (
    <ModalShell title="Edit booking" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name" data-testid="edit-name" value={f.name} onChange={set("name")} />
        <Field label="Email" data-testid="edit-email" type="email" value={f.email} onChange={set("email")} />
        <Field label="Phone" data-testid="edit-phone" value={f.phone} onChange={set("phone")} />
        <Field label="Address" data-testid="edit-address" value={f.address} onChange={set("address")} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="ZIP" data-testid="edit-zip" value={f.zip} onChange={set("zip")} />
          <Field label="Pickup day" data-testid="edit-pickup" value={f.pickup_day} onChange={set("pickup_day")} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Visit date" data-testid="edit-date" type="date" value={f.slot_date} onChange={set("slot_date")} />
          <label className="block">
            <span className="text-sm font-semibold">Time window</span>
            <select data-testid="edit-time" value={f.slot_time} onChange={set("slot_time")}
              className="mt-1 w-full rounded-xl border border-border bg-muted/50 px-3 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary">
              {SLOT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>
        {avail && (
          <p data-testid="edit-slot-availability" className={`text-sm font-semibold ${avail.full ? "text-destructive" : "text-muted-foreground"}`}>
            {avail.full
              ? `⚠️ This slot is full — ${avail.booked} of ${avail.capacity} booked. You can still override.`
              : `This slot has ${avail.available} of ${avail.capacity} openings left.`}
          </p>
        )}
        <Field label="Number of bins" data-testid="edit-bins" type="number" min="1" max="10" value={f.num_bins} onChange={set("num_bins")} />
        <label className="block">
          <span className="text-sm font-semibold">Notes</span>
          <textarea data-testid="edit-notes" rows={3} value={f.notes} onChange={set("notes")}
            className="mt-1 w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
        </label>
        <button data-testid="edit-booking-save" type="submit" className="mt-2 w-full rounded-xl bg-primary px-6 py-3 font-bold text-primary-foreground hover:brightness-110">Save changes</button>
      </form>
    </ModalShell>
  );
}

function EditCustomerModal({ customer, onClose, onSave }) {
  const [f, setF] = useState({ name: customer.name || "", phone: customer.phone || "", email: customer.email || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => { e.preventDefault(); onSave(customer.id, f); };
  return (
    <ModalShell title="Edit customer" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name" data-testid="edit-cust-name" value={f.name} onChange={set("name")} />
        <Field label="Email" data-testid="edit-cust-email" type="email" value={f.email} onChange={set("email")} />
        <Field label="Phone" data-testid="edit-cust-phone" value={f.phone} onChange={set("phone")} />
        <button data-testid="edit-customer-save" type="submit" className="mt-2 w-full rounded-xl bg-primary px-6 py-3 font-bold text-primary-foreground hover:brightness-110">Save changes</button>
      </form>
    </ModalShell>
  );
}
